import * as React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import { TEXT_BODY, TEXT_HEADING } from "../../styles/homepageSections";

export type RowsSectionHeaderProps = {
  title: string;
  subheadline?: string | null;
  description?: string | null;
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    root: {
      textAlign: "center",
      padding: "1rem",
      maxWidth: 800,
      margin: "0 auto",
    },
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    title: {
      color: TEXT_HEADING,
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 2rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
      },
    },
    titleAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    description: {
      color: TEXT_BODY,
      fontSize: 18,
      lineHeight: 1.55,
      maxWidth: 720,
      margin: "0 auto",
      "& p": {
        margin: 0,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
  })
);

const hasContent = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const RowsSectionHeader: React.FC<RowsSectionHeaderProps> = ({
  title,
  subheadline,
  description,
}) => {
  const classes = useStyles();

  if (!title) {
    return null;
  }

  return (
    <div className={classes.root}>
      {hasContent(subheadline) && (
        <Typography component="p" className={classes.subheadline}>
          {subheadline}
        </Typography>
      )}
      <Typography
        variant="h2"
        component="h2"
        className={[
          classes.title,
          hasContent(subheadline) && classes.titleAfterSubheadline,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {title}
      </Typography>
      {hasContent(description) && (
        <div className={classes.description}>
          <ReactMarkdown
            linkTarget="_blank"
            components={{
              p: ({ children }) => <p>{children}</p>,
            }}
          >
            {description!}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default RowsSectionHeader;
